import {
  NavigateFunction,
} from 'react-router-dom';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';

type SearchParamsUpdate = (searchParams: URLSearchParams) => void;

type QueuedUpdate = {
  update: SearchParamsUpdate;
  pathname: string;
};

const queue: QueuedUpdate[] = [];
let pendingNavigate: NavigateFunction | null = null;

function flush() {
  const navigate = pendingNavigate;
  const updates = queue.splice(0);
  pendingNavigate = null;

  if (isNil(navigate)) {
    return;
  }

  const { pathname, search, hash } = window.location;
  const searchParams = new URLSearchParams(search);
  const currentSearch = searchParams.toString();

  updates.forEach(({ update, pathname: queuedPathname }) => {
    // The route was left after this update was queued
    if (queuedPathname === pathname) {
      update(searchParams);
    }
  });

  const nextSearch = searchParams.toString();

  // A blocker (usePrompt) may cancel this navigation, so callers must react to
  // the resulting location rather than assume the write happened.
  if (nextSearch !== currentSearch) {
    navigate({
      search: notEmptyString(nextSearch) ? `?${nextSearch}` : '',
      hash,
    }, { replace: true });
  }
}

/**
 * Queues an update of the current URL's search params. Updates queued in the
 * same tick are applied in order to one draft and written with a single
 * replace navigation. Each update is scoped to the pathname at queue time and
 * dropped if that route was left before the flush.
 */
export function queueUrlParamsUpdate(
  update: SearchParamsUpdate,
  navigate: NavigateFunction,
) {
  const isFirstUpdate = queue.length === 0;
  queue.push({ update, pathname: window.location.pathname });
  pendingNavigate = navigate;

  if (isFirstUpdate) {
    queueMicrotask(flush);
  }
}

/** Sets each key to its value, or deletes it when the value is null. */
export function patchSearchParams(
  values: Record<string, string | null>,
  navigate: NavigateFunction,
) {
  queueUrlParamsUpdate((searchParams) => {
    Object.entries(values).forEach(([key, value]) => {
      if (isNil(value)) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
    });
  }, navigate);
}
